import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { DateTimeHelper } from '../helpers/datetime';
import { PageHelperService } from '../helpers/page-helper.service';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import { CustomerSettings } from '../services/customer-settings-service';
import { DamageService, DeliveryImagesModel } from 'src/shared/service-proxies/service-proxies';

type DeliveryImageItem = {
  label: string;
  url: string;
};

@Component({
    selector: 'app-delivery-images',
    templateUrl: './delivery-images.component.html',
    styleUrls: ['./delivery-images.component.scss'],
    standalone: false
})
export class DeliveryImagesComponent extends PagebaseComponent implements OnInit {
  loading: boolean = false;
  deliveryImages: DeliveryImagesModel | null = null;
  imageLinks: DeliveryImageItem[] = [];
  errorMessage: string | null = null;
  isPopupOpen: boolean = false;
  selectedImage: string | null = null;

  private firstInit: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private damageService: DamageService,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    customerSettings: CustomerSettings) {
      super(pageHelperService, applicationStatusService, customerSettings, "Delivery Photographs");
    }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(() => {
      if (!this.firstInit) {
        this.getData(false);
      }
    });
    this.firstInit = false;
  }

  getData(companyChange: boolean): void {
    const params = this.route.snapshot.queryParamMap;
    const assetId = params.get('assetId');
    const arrivedAtValue = params.get('arrivedAt');
    const locationId = params.get('locationId');
    const arrivedAt = arrivedAtValue ? moment(arrivedAtValue, moment.ISO_8601, true) : null;

    this.errorMessage = null;
    this.deliveryImages = null;
    this.imageLinks = [];
    this.loading = false;
    this.setIsLoading(this.loading);

    if (!arrivedAtValue || !arrivedAt || !arrivedAt.isValid()) {
      this.errorMessage = "Missing or invalid arrivedAt query parameter (ISO date).";
      return;
    }

    this.loading = true;
    this.setIsLoading(this.loading);
    this.pageHelperService.setPageTitle("Delivery Photographs");

    this.damageService.deliveryImages(assetId, arrivedAt, locationId).subscribe({
      next: data => {
        this.deliveryImages = data;
        this.imageLinks = this.buildImageLinks(data);
        /*
        if (data?.deliveryConfirmation?.deliveryLocation) {
          this.pageHelperService.setPageTitle(`Delivery Photographs - ${data?.deliveryConfirmation?.deliveryLocation}`);
        }
        */
        this.loading = false;
        this.setIsLoading(this.loading);
      },
      error: () => {
        this.errorMessage = "Unable to load delivery photographs.";
        this.loading = false;
        this.setIsLoading(this.loading);
      }
    });
  }

  reset(): void {
    this.deliveryImages = null;
    this.imageLinks = [];
    this.errorMessage = null;
  }

  formatDateAndTime(date: moment.Moment | undefined): string {
    return DateTimeHelper.formatDateAndTime(date);
  }

  formatDate(date: moment.Moment | undefined): string {
    return DateTimeHelper.formatDate(date);
  }

  openPopup(imageSrc: string): void {
    this.selectedImage = imageSrc;
    this.isPopupOpen = true;
  }

  closePopup(): void {
    this.isPopupOpen = false;
  }

  private buildImageLinks(data: DeliveryImagesModel | null): DeliveryImageItem[] {
    const confirmation = data?.deliveryConfirmation;
    const links = [
      { label: "Image 1", url: confirmation?.deliveryImage1Link },
      { label: "Image 2", url: confirmation?.deliveryImage2Link },
      { label: "Image 3", url: confirmation?.deliveryImage3Link },
      { label: "Image 4", url: confirmation?.deliveryImage4Link }
    ];

    return links.filter(link => !!link.url) as DeliveryImageItem[];
  }
}
